import React, { lazy, Suspense, useEffect, useRef } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { GlobalErrorProvider } from "./components/providers/GlobalErrorProvider";
import { AccessibilityAudit } from "./components/AccessibilityAudit";
import { FeatureFlagProvider } from "./contexts/FeatureFlagContext";
import { ToastProvider } from "./components/socialWorker/ToastProvider";
import ProtectedRoute from "./components/ProtectedRoute";
import { createConditionalLazy } from "./utils/conditionalImports";
import { initMigrations } from "./utils/storageMigrations";
import { useAuthStore, type User } from "./stores/authStore";
import { useCommercialEntitlements } from "./hooks/useCommercialEntitlements";
import { authAPI } from "./services/api";
import {
  productAnalytics,
  getAnalyticsConsent,
} from "./config/productAnalytics";
import { AnalyticsConsentBanner } from "./components/common/AnalyticsConsentBanner";
import { getApiUrl } from "./config/env";
import { ensureCsrfToken } from "./utils/csrfToken";
import {
  ProviderLegacyLocationsDetailRedirect,
  ProviderLegacyLocationsListRedirect,
} from "./components/provider/ProviderLegacyLocationsRedirect";
// Lazy load components using the new lazy components
import {
  LazyProviderDetailPage,
  LazyProviderListPage,
  LazyRegisterPage,
  LazyProviderRegistrationFlow,
} from "./components/lazy/LazyComponents";

const HomePage = lazy(() => import("./pages/HomePage"));
// Use conditional imports for hidden dashboards
// These will only be bundled when feature flags are enabled
const ProviderDashboard = createConditionalLazy(
  () => import("./pages/ProviderDashboard"),
  "PROVIDER_DASHBOARD"
);
// PatientDashboard: new widget-based provider-search dashboard — always available
const PatientDashboard = lazy(() => import("./pages/PatientDashboard"));
// PatientHealthPortal: full health records / medications / appointments portal (feature-flagged)
const PatientHealthPortal = createConditionalLazy(
  () => import("./pages/PatientHealthPortal"),
  "PATIENT_DASHBOARD"
);
const SocialWorkerDashboard = lazy(
  () => import("./pages/SocialWorkerDashboard")
);
const ProviderFavoritesPage = lazy(
  () => import("./pages/ProviderFavoritesPage")
);
const SearchAnalyticsPage = lazy(() => import("./pages/SearchAnalyticsPage"));
const SearchHistoryPage = lazy(() => import("./pages/SearchHistoryPage"));
const SavedSearchesPage = lazy(() => import("./pages/SavedSearchesPage"));
const CasesPage = lazy(() => import("./pages/CasesPage"));
const CareCoordinatorAccountBillingPage = lazy(
  () => import("./pages/CareCoordinatorAccountBillingPage")
);
const CareCoordinatorAccountLayout = lazy(
  () => import("./components/account/CareCoordinatorAccountLayout")
);
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const LoginPage = lazy(() => import("./pages/LoginPage"));
const ForgotPasswordPage = lazy(() => import("./pages/ForgotPasswordPage"));
const ResetPasswordPage = lazy(() => import("./pages/ResetPasswordPage"));
const VerifyEmailPage = lazy(() => import("./pages/VerifyEmailPage"));
const ScheduleAppointmentPage = lazy(
  () => import("./pages/ScheduleAppointmentPage")
);
const UnauthorizedPage = lazy(() => import("./pages/UnauthorizedPage"));
const PrivacyPolicyPage = lazy(() => import("./pages/PrivacyPolicyPage"));
const TermsOfServicePage = lazy(() => import("./pages/TermsOfServicePage"));
const HelpHubPage = lazy(() => import("./pages/help/HelpHubPage"));
const HelpArticlePage = lazy(() => import("./pages/help/HelpArticlePage"));

// Provider components
const ProviderLayout = lazy(
  () => import("./components/provider/ProviderLayout")
);
const ProviderBillingAccess = lazy(
  () => import("./components/provider/ProviderBillingAccess")
);
const ProviderProfile = lazy(
  () => import("./components/provider/ProviderProfile")
);
const ProviderLocationsPage = lazy(
  () => import("./pages/ProviderLocationsPage")
);
const ProviderLocationDetailPage = lazy(
  () => import("./pages/ProviderLocationDetailPage")
);
const ProviderAddFacilityPage = lazy(
  () => import("./pages/ProviderAddFacilityPage")
);
const ProviderOrganizationSettingsPage = lazy(
  () => import("./pages/ProviderOrganizationSettingsPage")
);
const InviteAcceptPage = lazy(() => import("./pages/InviteAcceptPage"));
const InviteRegisterPage = lazy(() => import("./pages/InviteRegisterPage"));
const AgencyInvitationAcceptPage = lazy(
  () => import("./pages/AgencyInvitationAcceptPage")
);
const AgencyInvitationRegisterPage = lazy(
  () => import("./pages/AgencyInvitationRegisterPage")
);
const AgencyTeamManagementPage = lazy(
  () => import("./pages/AgencyTeamManagementPage")
);
const AgencyOwnerSignupPage = lazy(
  () => import("./pages/AgencyOwnerSignupPage")
);
const CaseShareStatusPage = lazy(() => import("./pages/CaseShareStatusPage"));
const UnsubscribePage = lazy(() => import("./pages/UnsubscribePage"));
const ProviderPoliciesHubPage = lazy(
  () => import("./pages/ProviderPoliciesHubPage")
);
const ProviderFacilityPoliciesPage = lazy(
  () => import("./pages/ProviderFacilityPoliciesPage")
);

const PatientProfile = lazy(() => import("./components/PatientProfile"));

// Loading component
const Loading = () => (
  <div className="flex items-center justify-center min-h-app">
    <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
  </div>
);

// Main App component
function App() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const user = useAuthStore((s) => s.user);
  useCommercialEntitlements();
  const sessionValidatedRef = useRef(false);
  const wasAuthenticatedRef = useRef(false);

  // Initialize storage migrations and care taxonomy sync on app startup
  useEffect(() => {
    initMigrations();
    void import("./utils/careTaxonomySync").then(
      ({ syncCareTaxonomyFromBackend }) => syncCareTaxonomyFromBackend()
    );
  }, []);

  // Privacy-safe usage ping: once per session when user has not opted in to full analytics.
  // No cookies, no session ID sent; backend just counts requests for aggregate "sessions" metric.
  // One retry after 2s on failure to improve coverage (e.g. slow network); then mark sent so we don't retry again.
  useEffect(() => {
    if (getAnalyticsConsent() === "true") return;
    try {
      if (
        typeof window !== "undefined" &&
        window.sessionStorage?.getItem("usage_ping_sent") === "1"
      )
        return;
      const markSent = (): void => {
        try {
          window.sessionStorage?.setItem("usage_ping_sent", "1");
        } catch {
          // ignore
        }
      };
      const url = getApiUrl("/usage/ping");
      const doPing = (): Promise<Response> =>
        fetch(url, { method: "GET", credentials: "omit" });
      doPing()
        .then((res) => {
          if (res.ok) {
            markSent();
          } else if (typeof window !== "undefined") {
            window.setTimeout(() => doPing().finally(markSent), 2000);
          }
        })
        .catch(() => {
          if (typeof window !== "undefined") {
            window.setTimeout(() => doPing().finally(markSent), 2000);
          }
        });
    } catch {
      // analytics should never break the app
    }
  }, []);

  // Do not send database user IDs, or browser-derived hashes of them, to a
  // third-party analytics provider. Reset the anonymous analytics session when
  // an authenticated user signs out so sessions are never joined across users.
  useEffect(() => {
    if (isAuthenticated) {
      wasAuthenticatedRef.current = true;
      return;
    }
    if (wasAuthenticatedRef.current) {
      try {
        productAnalytics.reset();
      } catch {
        // analytics must not break logout
      }
      // Dynamic import avoids static TDZ in the entry bundle (minifier + chunk cycles can
      // surface as "Cannot access 'C' before initialization" on some hosts/CDNs).
      void import("./lib/sessionClear").then((m) => m.clearSessionId());
      wasAuthenticatedRef.current = false;
    }
  }, [isAuthenticated]);

  // Cookie-session SPA mutations need CSRF before first POST (prod/staging),
  // including anonymous flows such as provider registration.
  useEffect(() => {
    void ensureCsrfToken().catch(() => {
      // Non-fatal; next GET may still cache a token from response headers
    });
  }, []);

  // Validate session when app loads with rehydrated auth (e.g. after refresh or backend restart).
  // If the cookie/token is invalid, GET /auth/me returns 401 and the apiClient interceptor
  // clears auth and redirects to login, so the user is not left in a stale "logged in" state.
  // On success, refresh profile fields (email/name are not kept in localStorage).
  useEffect(() => {
    if (!isAuthenticated) {
      sessionValidatedRef.current = false;
      return;
    }
    if (sessionValidatedRef.current) return;
    sessionValidatedRef.current = true;
    void ensureCsrfToken().catch(() => {
      // Non-fatal; next GET may still cache a token from response headers
    });
    authAPI
      .getCurrentUser()
      .then((me) => {
        if (me && typeof me === "object") {
          useAuthStore.getState().updateUser(me as User);
        }
      })
      .catch(() => {
        // Interceptor handles 401 (clearAuth, redirect). Ignore here to avoid unhandled rejection.
      });
  }, [isAuthenticated]);

  return (
    <GlobalErrorProvider>
      <FeatureFlagProvider>
        <ToastProvider>
          <HelmetProvider>
            <Router
              future={{
                v7_startTransition: true,
                v7_relativeSplatPath: true,
              }}
            >
              <div className="flex flex-col flex-1 min-h-0 min-w-0 max-w-full overflow-x-clip bg-gray-50">
                {/* Development-only accessibility audit */}
                {process.env.NODE_ENV === "development" && (
                  <AccessibilityAudit enabled={true} showDetails={true} />
                )}
                <Suspense fallback={<Loading />}>
                  <Routes>
                    {/* Public routes */}
                    <Route path="/" element={<HomePage />} />
                    <Route
                      path="/providers"
                      element={<LazyProviderListPage />}
                    />
                    <Route
                      path="/providers/:id"
                      element={<LazyProviderDetailPage />}
                    />

                    <Route path="/login" element={<LoginPage />} />
                    <Route
                      path="/forgot-password"
                      element={<ForgotPasswordPage />}
                    />
                    <Route
                      path="/reset-password"
                      element={<ResetPasswordPage />}
                    />
                    <Route path="/verify-email" element={<VerifyEmailPage />} />
                    <Route
                      path="/invite/accept"
                      element={<InviteAcceptPage />}
                    />
                    <Route
                      path="/invite/register"
                      element={<InviteRegisterPage />}
                    />
                    <Route
                      path="/agency/invitations/accept"
                      element={<AgencyInvitationAcceptPage />}
                    />
                    <Route
                      path="/agency/invitations/register"
                      element={<AgencyInvitationRegisterPage />}
                    />
                    <Route
                      path="/s/c/:token"
                      element={<CaseShareStatusPage />}
                    />
                    <Route path="/unsubscribe" element={<UnsubscribePage />} />
                    <Route path="/register" element={<LazyRegisterPage />} />
                    <Route
                      path="/register/agency-owner"
                      element={<AgencyOwnerSignupPage />}
                    />
                    <Route
                      path="/provider-registration"
                      element={<LazyProviderRegistrationFlow />}
                    />
                    <Route
                      path="/unauthorized"
                      element={<UnauthorizedPage />}
                    />
                    <Route
                      path="/privacy-policy"
                      element={<PrivacyPolicyPage />}
                    />
                    <Route
                      path="/terms-of-service"
                      element={<TermsOfServicePage />}
                    />
                    <Route path="/help" element={<HelpHubPage />} />
                    <Route path="/help/*" element={<HelpArticlePage />} />

                    {/* Social Worker - Early placement for proper routing */}
                    <Route
                      path="/social-worker-dashboard"
                      element={
                        <ProtectedRoute
                          allowedRoles={["social_worker", "admin"]}
                          fallbackPath="/"
                        >
                          <SocialWorkerDashboard />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/agency/team"
                      element={<Navigate to="/account/team" replace />}
                    />
                    <Route
                      path="/provider-favorites"
                      element={
                        <ProtectedRoute
                          allowedRoles={["social_worker", "admin", "patient"]}
                          fallbackPath="/"
                        >
                          <ProviderFavoritesPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/search-analytics"
                      element={
                        <ProtectedRoute
                          allowedRoles={["social_worker", "admin"]}
                          fallbackPath="/"
                        >
                          <SearchAnalyticsPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/search-history"
                      element={
                        <ProtectedRoute
                          allowedRoles={["social_worker", "admin", "patient"]}
                          fallbackPath="/"
                        >
                          <SearchHistoryPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/saved-searches"
                      element={
                        <ProtectedRoute
                          allowedRoles={["social_worker", "admin", "patient"]}
                          fallbackPath="/"
                        >
                          <SavedSearchesPage />
                        </ProtectedRoute>
                      }
                    />

                    {/* Cases Management Routes */}
                    <Route
                      path="/cases"
                      element={
                        <ProtectedRoute
                          allowedRoles={["social_worker", "admin"]}
                          fallbackPath="/"
                        >
                          <CasesPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/cases/new"
                      element={
                        <ProtectedRoute
                          allowedRoles={["social_worker", "admin"]}
                          fallbackPath="/"
                        >
                          <CasesPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/cases/:caseId"
                      element={
                        <ProtectedRoute
                          allowedRoles={["social_worker", "admin"]}
                          fallbackPath="/"
                        >
                          <CasesPage />
                        </ProtectedRoute>
                      }
                    />

                    <Route
                      path="/account"
                      element={
                        <ProtectedRoute
                          allowedRoles={["social_worker"]}
                          fallbackPath="/unauthorized"
                        >
                          <CareCoordinatorAccountLayout />
                        </ProtectedRoute>
                      }
                    >
                      <Route
                        index
                        element={<Navigate to="billing" replace />}
                      />
                      <Route
                        path="billing"
                        element={<CareCoordinatorAccountBillingPage />}
                      />
                      <Route
                        path="team"
                        element={<AgencyTeamManagementPage />}
                      />
                    </Route>
                    {/* Legacy / mistyped URL from registration → canonical provider dashboard */}
                    <Route
                      path="/provider-dashboard"
                      element={<Navigate to="/provider/dashboard" replace />}
                    />

                    <Route
                      path="/provider/facilities/:providerId"
                      element={
                        <ProtectedRoute
                          allowedRoles={["provider", "provider_staff"]}
                          fallbackPath="/login"
                        >
                          <ProviderLocationDetailPage />
                        </ProtectedRoute>
                      }
                    />

                    {/* New Provider Routes with Layout */}
                    <Route path="/provider" element={<ProviderLayout />}>
                      <Route
                        index
                        element={<Navigate to="facilities" replace />}
                      />
                      <Route
                        path="dashboard"
                        element={
                          <ProtectedRoute
                            featureFlag="PROVIDER_DASHBOARD"
                            fallbackPath="/provider/facilities"
                          >
                            <ProviderDashboard />
                          </ProtectedRoute>
                        }
                      />
                      <Route path="profile" element={<ProviderProfile />} />
                      <Route
                        path="organization"
                        element={<ProviderOrganizationSettingsPage />}
                      />
                      <Route
                        path="facilities"
                        element={<ProviderLocationsPage />}
                      />
                      <Route
                        path="facilities/new"
                        element={<ProviderAddFacilityPage />}
                      />
                      <Route
                        path="facilities/:providerId/policies"
                        element={<ProviderFacilityPoliciesPage />}
                      />
                      <Route
                        path="policies"
                        element={<ProviderPoliciesHubPage />}
                      />
                      <Route
                        path="billing"
                        element={
                          <ProtectedRoute
                            allowedRoles={["provider", "provider_staff"]}
                            fallbackPath="/unauthorized"
                          >
                            <ProviderBillingAccess>
                              <CareCoordinatorAccountBillingPage />
                            </ProviderBillingAccess>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="locations"
                        element={<ProviderLegacyLocationsListRedirect />}
                      />
                      <Route
                        path="locations/:providerId"
                        element={<ProviderLegacyLocationsDetailRedirect />}
                      />
                    </Route>

                    <Route
                      path="/patient-dashboard"
                      element={
                        <ProtectedRoute
                          allowedRoles={["patient", "admin"]}
                          fallbackPath="/login"
                        >
                          <PatientDashboard />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/my-health-portal"
                      element={
                        <ProtectedRoute
                          featureFlag="PATIENT_DASHBOARD"
                          allowedRoles={[
                            "patient",
                            "admin",
                          ]}
                          fallbackPath="/"
                        >
                          <PatientHealthPortal />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/patient-profile"
                      element={
                        <ProtectedRoute
                          allowedRoles={["patient"]}
                          fallbackPath="/login"
                        >
                          <PatientProfile />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin-dashboard"
                      element={
                        <ProtectedRoute allowedRoles={["admin"]}>
                          <AdminDashboard />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/schedule-appointment"
                      element={
                        <ProtectedRoute
                          allowedRoles={["patient", "admin"]}
                          fallbackPath="/login"
                        >
                          <ScheduleAppointmentPage />
                        </ProtectedRoute>
                      }
                    />
                  </Routes>
                </Suspense>
                <AnalyticsConsentBanner />
              </div>
            </Router>
          </HelmetProvider>
        </ToastProvider>
      </FeatureFlagProvider>
    </GlobalErrorProvider>
  );
}

export default App;
